(ns machtfit.product-form.for-whom
  (:require [machtfit.dashboard-widgets :as widgets]
            [machtfit.employee-rules-table :refer [employee-rules-table]]
            [machtfit.product-form.core :as product-form]
            [machtfit.product-form.review :as review]
            [machtfit.product-form.section :refer [show-next-section]]
            [machtfit.product-form.when :as when]
            [machtfit.utils :as utils]
            [re-frame.core :as rf]))

(rf/reg-event-db ::set-min-participants-value
  (fn [db [_ new-value]]
    (-> db
        (assoc-in [:product :for-whom :min-participants]
                  (let [v (int new-value)]
                    {:choice (if (>= v 1) :set :unset) :value v}))
        (assoc-in [:ui :last-focused-config-section] :for-whom))))

(rf/reg-event-db ::set-min-participants-choice
  (fn [db [_ new-choice]]
    (-> db
        (update-in [:product :for-whom :min-participants]
                   #(if (= :set new-choice)
                      {:choice :set :value (if (>= (:value %) 1) (:value %) 2)}
                      {:choice :unset :value (:value %)}))
        (assoc-in [:ui :last-focused-config-section] :for-whom))))

(defn- min-participants []
  (let [{:keys [choice value]} @(rf/subscribe [:get [:product :for-whom :min-participants]])
        course? (= :multiple-sessions @(rf/subscribe [:get [:product :when :sessions-per-signup]]))
        singular? (= 1 (if course? @(rf/subscribe [::when/num-schedules]) @(rf/subscribe [::when/num-slots])))
        verb-singular? (or singular? (and (= choice :set) (not course?) (not singular?)))
        intro (case [course? singular?]
                [true true] "Der Kurs"
                [true false] "Die Kurse"
                [false true] "Der Termin"
                [false false] (if (= :set choice) "Jeder Termin" "Die Termine"))
        verb (if verb-singular? "findet" "finden")]

    [:div.col.s12.valign-wrapper {:style {:margin-bottom "1rem"}}
     [widgets/choice
      {:name "min-participants"
       :intro intro
       :choices [[:unset (str verb " auf jeden Fall statt.")]
                 [:set [:span (str verb " nur statt, falls mindestens ")
                        [widgets/inline-input
                         {:value (case choice :set value "")
                          :on-save #(rf/dispatch [::set-min-participants-value %])}]
                        (if (= 1 value) " Person teilnimmt." " Personen teilnehmen.")]]]
       :current-choice choice
       :on-select #(rf/dispatch [::set-min-participants-choice %])}]]))

(rf/reg-event-db ::set-max-participants-value
  (fn [db [_ new-value]]
    (-> db
        (assoc-in [:product :for-whom :max-participants]
                  (let [v (int new-value)]
                    {:choice (if (>= v 1) :set :unset) :value v}))
        (assoc-in [:ui :last-focused-config-section] :for-whom))))

(rf/reg-event-db ::set-max-participants-choice
  (fn [db [_ new-choice]]
    (-> db
        (update-in [:product :for-whom :max-participants]
                   #(let [current-value (:value %)
                          min-participants-value (get-in db [:product :for-whom :min-participants :value])
                          default-value (cond (nil? min-participants-value) 15
                                              (>= min-participants-value 1) (* 2 min-participants-value)
                                              :else 15)]
                      (if (= :set new-choice)
                        {:choice :set :value (cond
                                               (nil? current-value) default-value
                                               (>= current-value 1) current-value
                                               :else default-value)}
                        {:choice :unset :value current-value})))
        (assoc-in [:ui :last-focused-config-section] :for-whom))))

(defn- max-participants []
  (let [max-participants' @(rf/subscribe [:get [:product :for-whom :max-participants]])
        num-schedules @(rf/subscribe [::when/num-schedules])
        num-slots @(rf/subscribe [::when/num-slots])
        sessions-per-signup @(rf/subscribe [:get [:product :when :sessions-per-signup]])
        course? (= :multiple-sessions sessions-per-signup)
        singular? (= 1 (if course? num-schedules num-slots))
        {:keys [choice value]} max-participants'
        max-parts-singular? (= 1 value)
        intro (case [course? singular?]
                [true true] "Am Kurs"
                [true false] "An jedem Kurs"
                [false true] "Am Termin"
                [false false] "Pro Termin")]
    [:div.col.s12.valign-wrapper {:style {:margin-bottom "1rem"}}
     [widgets/choice
      {:name "max-participants"
       :intro intro
       :choices [[:unset "können unbegrenzt viele Personen"]
                 [:set [:span (str (if max-parts-singular? "kann" "können")) " höchstens "
                        [widgets/inline-input
                         {:value (case choice :set value "")
                          :on-save #(rf/dispatch [::set-max-participants-value %])}]
                        (str (if max-parts-singular? " Person" " Personen"))]]]
       :current-choice choice
       :on-select #(rf/dispatch [::set-max-participants-choice %])
       :outro "teilnehmen."}]]))

(rf/reg-sub ::managed-employees-unless-machtfit-admin
  :<- [::product-form/machtfit-admin?]
  :<- [:get [:user :managed-employees]]

  (fn [[machtfit-admin? managed-employees] _]
    (when-not machtfit-admin? managed-employees)) ;; The selectable options in the employee rules table for allowed employees should not be
          ;; filtered by the user's managed employees rules, when she is a machtfit admin.
  )

(rf/reg-event-db ::add-allowed-employees-rule
  (fn [db [_ rule]]
    (update-in db [:product :for-whom :allowed-employees] #(conj (if (empty? %) [] %) rule))))

(rf/reg-event-db ::delete-allowed-employees-rule
  (fn [db [_ index]]
    (update-in db [:product :for-whom :allowed-employees] utils/delete-at index)))

(rf/reg-event-db ::set-allowed-employees-condition
  (fn [db [_ index condition value]]
    (update-in db [:product :for-whom :allowed-employees index :condition]
               #(if (or (and (coll? value) (empty? value)) (nil? value))
                  (dissoc % condition)
                  (assoc % condition value)))))

(defn- offered-for []
  [:div.col.s12
   [employee-rules-table {:id :offered-for
                          :allow-no-rules? (:is_supplier @(rf/subscribe [:get [:ui :partner]]))
                          :rules-must-match-admin-rules? (not @(rf/subscribe [::product-form/machtfit-admin?]))
                          :placeholder-text (if @(rf/subscribe [:get [:options :partner-product?]])
                                              [:<>
                                               [:p "Das Angebot wird allen Mitarbeitern im machtfit-Bereich angezeigt."]
                                               [:p "Sie können das Angebot auch nur für bestimmte Mitarbeiter — dann im Firmenbereich — anzeigen lassen, indem Sie nachfolgend Bedingungen für diese Mitarbeiter angeben."]]
                                              [:p "Allen Mitarbeiter in allen Unternehmen."])
                          :rules-sub [:get [:product :for-whom :allowed-employees]]
                          :companies-sub [:get [:ui :employee-rules :condition-options :companies]]
                          :places-of-business-sub [:get [:ui :employee-rules :condition-options :places-of-business]]
                          :employee-groups-sub [:get [:ui :employee-rules :condition-options :employee-groups]]
                          :subsidiaries-sub [:get [:ui :employee-rules :condition-options :subsidiaries]]
                          :occupation-groups-sub [:get [:ui :employee-rules :condition-options :occupation-groups]]
                          :managed-employees-sub [::managed-employees-unless-machtfit-admin]
                          :on-delete-rule ::delete-allowed-employees-rule
                          :on-add-rule ::add-allowed-employees-rule
                          :on-set-rule-condition ::set-allowed-employees-condition
                          :places-of-business-selective-intro-text "Das Angebot wird"
                          :places-of-business-selective-outro-text "angezeigt."
                          :places-of-business-not-selective-text "an allen (auch zukünftigen) Standorten"
                          :places-of-business-selective-text "nur an ausgewählten Standorten"
                          :employee-groups-selective-intro-text "Das Angebot wird"
                          :employee-groups-selective-outro-text "angezeigt."
                          :employee-groups-not-selective-text "allen (auch zukünftigen) Mitarbeitergruppen"
                          :employee-groups-selective-text "nur ausgewählten Mitarbeitergruppen"}]])

(defn- audience-gender []
  [:div>div.col.s12.valign-wrapper
   [:h6 {:style {:padding-right "1rem"}}
    "Das Angebot richtet sich"]
   [:div
    [widgets/radio-options
     {:name "audience-gender"
      :choices [[nil "gleichermaßen an Frauen und Männer."]
                [:F "nur an Frauen."]
                [:M "nur an Männer."]]
      :current-value @(rf/subscribe [:get [:product :for-whom :audience-gender]])
      :on-select #(rf/dispatch [:set-path [:product :for-whom :audience-gender] %])}]]])

(defn- area-independent-access []
  [:div.col.s12
   [:input.filled-in {:id "area-independent-access"
                      :type "checkbox"
                      :checked @(rf/subscribe [:get [:product :for-whom :area-independent-access]])
                      :on-change #(rf/dispatch [:toggle [:product :for-whom :area-independent-access]])}]
   [:label {:for "area-independent-access" :style {:color "black"}}
    "Angebot für alle Nutzer:innen sichtbar."]])

(defn- bookable-only-once []
  [:div.col.s12
   [:input.filled-in {:id "bookable-only-once"
                      :type "checkbox"
                      :checked @(rf/subscribe [:get [:product :for-whom :bookable-only-once?]])
                      :on-change #(rf/dispatch [:toggle [:product :for-whom :bookable-only-once?]])}]
   [:label {:for "bookable-only-once" :style {:color "black"}}
    "Angebot kann nur einmal gebucht werden. (das ist NICHT dasselbe wie ein Digital Deal)"]])

(rf/reg-event-db ::toggle-audience-age
  (fn [db [_ age-id]]
    (update-in
     db
     [:product :for-whom :audience-age]
     #(if (contains? % age-id) (disj % age-id) (conj % age-id)))))

(rf/reg-sub ::has-age?
  :<- [:get [:product :for-whom :audience-age]]

  (fn [audience-age [_ age]]
    (contains? audience-age age)))

(rf/reg-sub ::is-last-age?
  :<- [:get [:product :for-whom :audience-age]]

  (fn [audience-age _]
    (->> audience-age vals count (= 1))))

(defn- age-option [age title]
  (let [checked? @(rf/subscribe [::has-age? age])
        is_last? @(rf/subscribe [::is-last-age?])]
    [:li
     [:input.filled-in {:type "checkbox"
                        :id age
                        :disabled (and is_last? checked?)
                        :checked checked?
                        :on-change #(rf/dispatch [::toggle-audience-age age])}]
     [:label.black-text {:for age
                         :style {:height "auto"
                                 :line-height "normal"}} title]]))

(defn- audience-age []
  [:div>div.col.s12.valign-wrapper
   [:h6 {:style {:padding-right "1rem"}} "Das Angebot ist bestimmt für"]
   [:ul {:style {:margin 0}}
    [age-option :age15-17 "15—17 Jahre"]
    [age-option :age18-49 "18—49 Jahre"]
    [age-option :age50-69 "50—69 Jahre"]
    [age-option :age70 "ab 70 Jahre"]]])

(rf/reg-sub ::has-participant-limits?
  :<- [:get [:product :when :has-schedule?]]
  :<- [:get [:product :when :sessions-per-signup]]
  :<- [:get [:product :when :cohesion]]

  (fn [[has-schedule? sessions-per-signup cohesion] _]
    (and has-schedule?
         (or (and (= :multiple-sessions sessions-per-signup) (= :progressive cohesion))
             (= :one-session sessions-per-signup)))))

(defn section-for-whom []
  (let [can-set-allowed-employees? @(rf/subscribe [:get [:options :can-set-allowed-employees?]])
        can-decide-age? @(rf/subscribe [:get [:options :can-decide-age?]])
        has-participant-limits? @(rf/subscribe [::has-participant-limits?])
        machtfit-admin? @(rf/subscribe [::product-form/machtfit-admin?])
        divider [:div.divider.col.s12 {:style {:margin "1rem 0"}}]]
    [widgets/section
     {:anchor "for-whom"
      :title [review/highlighted-section-title :for-whom "Für wen"]
      :subtitle [:div "ist das Angebot?"
                 [review/field-badges :for-whom]]
      :content [:div

                [audience-gender]

                (when can-decide-age?
                  [:div
                   divider
                   [audience-age]])

                (when machtfit-admin?
                  [:div
                   divider
                   [area-independent-access]])

                (when machtfit-admin?
                  [:div
                   divider
                   [bookable-only-once]])

                (when has-participant-limits?
                  [:div
                   divider
                   [min-participants]])

                (when has-participant-limits?
                  [:div
                   divider
                   [max-participants]])

                (when can-set-allowed-employees?
                  [:div
                   divider
                   [offered-for]])

                [show-next-section :for-whom]]}]))
